import apiClient from "./apiClient";
import { registerPortalUser } from "./userManagementService";
import { USERMGMT_URL } from "../utils/envConfig";
import userManagementApi from "./userManagementApi";
const API_URL = `/patients`; // Use relative path since apiClient has baseURL
const USER_MGMT_URL = USERMGMT_URL;

const unwrapCreatedPatient = (data) => {
  if (!data || typeof data !== "object") return data;
  if (data.id != null || data.patientId != null) return data;
  if (data.data && typeof data.data === "object") return data.data;
  if (data.patient && typeof data.patient === "object") return data.patient;
  return data;
};

const resolveRequestOrganizationId = (organizationId) => {
  const parsed = Number(organizationId);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
};

const derivePortalPassword = () => "1234";

const normaliseName = (patient) => {
  if (patient?.name) return patient.name;
  const first = patient?.firstName || "";
  const last = patient?.lastName || "";
  const combined = `${first} ${last}`.trim();
  return combined || "Patient";
};

export const getAllPatients = async () => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    // If user has no organizationId, backend should use base table
    const response = await apiClient.get(API_URL);
    const data = response.data;

    // Always return an array — paginated/wrapped payloads left the patient map empty
    // and monthly Excel rows only had invoice name + visit date.
    if (Array.isArray(data)) return data;
    if (data && typeof data === "object") {
      const candidates = [
        data.data,
        data.content,
        data.items,
        data.patients,
        data.results,
        data.records,
      ];
      for (const candidate of candidates) {
        if (Array.isArray(candidate)) return candidate;
      }
    }
    return [];
  } catch (error) {
    // Handle case where organization-specific table doesn't exist yet or backend error
    if (error.response?.status === 500 && 
        (error.response?.data?.message?.includes("Failed to retrieve") ||
         error.response?.data?.message?.includes("from table") ||
         error.response?.data?.message?.includes("does not exist"))) {
      console.warn("Organization-specific table not found, returning empty array:", error.response.data.message);
      return []; // Return empty array if table doesn't exist
    }
    console.error("Error fetching patients:", error);
    throw error;
  }
};

export const registerPatient = async (patient, options = {}) => {
  const {
    createUserAccount = true,
    portalPassword,
    organizationId: optionOrganizationId,
  } = options;

  try {
    // Get organizationId from localStorage for user registration
    // Only include if it's a valid positive number (common users won't have it)
    let organizationId = null;
    if (optionOrganizationId != null && optionOrganizationId !== "" && Number(optionOrganizationId) > 0) {
      organizationId = Number(optionOrganizationId);
    }
    const storedUser = localStorage.getItem("user");
    if (!organizationId && storedUser) {
      try {
        const user = JSON.parse(storedUser);
        const orgId = user?.organizationId;
        if (orgId != null && orgId !== "" && !isNaN(orgId) && Number(orgId) > 0) {
          organizationId = Number(orgId);
        }
      } catch (e) {
        console.error("Error parsing stored user:", e);
      }
    }
    
    // If organizationId is still null, try to get from localStorage directly
    if (!organizationId) {
      const orgIdFromStorage = localStorage.getItem("organizationId");
      if (orgIdFromStorage && !isNaN(orgIdFromStorage) && Number(orgIdFromStorage) > 0) {
        organizationId = Number(orgIdFromStorage);
      }
    }

    // Default patient type to OUT when callers omit it (Basic/Intermediate always OUT;
    // Premium callers set IN explicitly on admission).
    const formattedPatient = {
      ...patient,
      patientType: patient?.patientType || patient?.patient_type || "OUT",
    };
    if (formattedPatient.patient_type !== undefined) {
      delete formattedPatient.patient_type;
    }
    // PatientDTO is camelCase-only; snake_case aliases cause 400 JSON parse errors.
    if (formattedPatient.user_id != null && formattedPatient.userId == null) {
      formattedPatient.userId = formattedPatient.user_id;
    }
    delete formattedPatient.user_id;

    let portalCredentials = null;

    if (
      createUserAccount &&
      !formattedPatient.userId &&
      formattedPatient.mobile
    ) {
      const userPayload = {
        fullName: normaliseName(formattedPatient),
        mobile: formattedPatient.mobile,
        password: portalPassword || derivePortalPassword(),
        email: formattedPatient.email || "",
        role: "patient",
        department: "",
      };
      
      // Include organizationId if available (similar to staff/doctor registration)
      if (organizationId) {
        userPayload.organizationId = organizationId;
      }

      try {
        const createdUser = await registerPortalUser(userPayload);
        formattedPatient.userId = createdUser?.id ?? formattedPatient.userId;
        if (createdUser?.id) {
          portalCredentials = {
            username: formattedPatient.mobile,
            password: userPayload.password,
          };
          
          // Workaround: Update organization_id if it's null in the response
          // This happens when the user management backend doesn't store organization_id during registration
          // Similar to how staff/doctor updates work
          if ((createdUser.organization_id === null || createdUser.organization_id === undefined) && organizationId) {
            try {
              // Use snake_case field names to match the updateUser endpoint format
              const updatePayload = {
                id: createdUser.id,
                full_name: normaliseName(formattedPatient),
                user_pwd: userPayload.password, // Include password (required field)
                user_role: "patient",
                email: formattedPatient.email || "",
                department: "",
                mobile: formattedPatient.mobile,
                organization_id: organizationId, // Include organization_id in update payload
              };
              
              try {
                await userManagementApi.post(
                  "/updateUser",
                  updatePayload
                );

                console.log(
                  "Updated organization_id for patient user:",
                  createdUser.id
                );
              } catch (updateError) {
                console.warn(
                  "Error updating organization_id for patient user:",
                  updateError
                );

                // Don't fail the whole patient creation
              }
            } catch (updateError) {
              console.warn("Error updating organization_id for patient user:", updateError);
              // Don't fail the whole operation if organization_id update fails
            }
          }
        }
      } catch (error) {
        console.error("User portal registration failed:", error?.message || error);
        throw error;
      }
    }

    // Staff create: interceptor puts organizationId on the POST body (original behaviour).
    // Portal create: pass organizationId as a query param so the row is written to {orgId}_patient
    // without changing interceptor routing for hospital staff.
    const portalOrganizationId = resolveRequestOrganizationId(optionOrganizationId);
    const response = await apiClient.post(
      API_URL,
      formattedPatient,
      portalOrganizationId
        ? { params: { organizationId: portalOrganizationId } }
        : undefined
    );
    const created = unwrapCreatedPatient(response.data);
    if (portalCredentials) {
      return {
        ...created,
        portalCredentials,
      };
    }
    return created;
  } catch (error) {
    console.error("Error registering patient:", error);
    throw error;
  }
};

export const updatePatient = async (id, patient) => {
  try {
    // Get organizationId from localStorage for user update
    let organizationId = null;
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        const orgId = user?.organizationId;
        if (orgId != null && orgId !== "" && !isNaN(orgId) && Number(orgId) > 0) {
          organizationId = Number(orgId);
        }
      } catch (e) {
        console.error("Error parsing stored user:", e);
      }
    }
    
    // If organizationId is still null, try to get from localStorage directly
    if (!organizationId) {
      const orgIdFromStorage = localStorage.getItem("organizationId");
      if (orgIdFromStorage && !isNaN(orgIdFromStorage) && Number(orgIdFromStorage) > 0) {
        organizationId = Number(orgIdFromStorage);
      }
    }

    // Fetch current patient data to get userId and old values for comparison
    let currentPatient = null;
    let userId = null;
    let oldName = null;
    let oldEmail = null;
    let oldMobile = null;
    
    try {
      currentPatient = await getPatientById(id);
      userId = currentPatient?.userId;
      oldName = currentPatient?.name;
      oldEmail = currentPatient?.email;
      oldMobile = currentPatient?.mobile;
    } catch (fetchError) {
      console.warn("Could not fetch current patient data for user update:", fetchError);
      // Continue with patient update even if we can't fetch current data
    }

    // Remove organizationId from patient object if it exists (apiClient will add it as query param)
    const { organizationId: patientOrgId, user_id: userIdSnake, ...patientData } = patient;
    if (userIdSnake != null && patientData.userId == null) {
      patientData.userId = userIdSnake;
    }
    
    // apiClient will automatically add organizationId as query parameter via interceptor
    // (not in body, as Patient DTO doesn't accept it in request body)
    const response = await apiClient.put(`${API_URL}/${id}`, patientData);
    
    // Update user table if userId exists and name, email, or mobile changed
    if (userId && currentPatient) {
      // Get updated values from response data (which has the final updated values) or from patientData
      const newName = response.data?.name || patientData.name || oldName;
      const newEmail = response.data?.email !== undefined ? response.data.email : (patientData.email !== undefined ? patientData.email : oldEmail);
      const newMobile = response.data?.mobile || patientData.mobile || oldMobile;
      
      // Check if any user-related fields changed
      const hasNameChanged = oldName !== newName;
      const hasEmailChanged = oldEmail !== newEmail;
      const hasMobileChanged = oldMobile !== newMobile;
      
      if (hasNameChanged || hasEmailChanged || hasMobileChanged) {
        try {
          // Use snake_case field names to match the updateUser endpoint format
          const updatePayload = {
            id: userId,
            full_name: newName || normaliseName({ name: newName }),
            user_role: "patient",
            email: newEmail || "",
            department: "",
            mobile: newMobile || "",
            // Include organization_id if available
            ...(organizationId && { organization_id: organizationId }),
          };
          
          // Note: We don't include user_pwd in update payload unless password is being changed
          // The user management backend may require it, so we'll try without it first
          // If it fails, we can fetch the current password or handle it differently
          
          try {
            await userManagementApi.post(
              "/updateUser",
              updatePayload
            );

            console.log(
              "Updated user table for patient:",
              userId
            );
          } catch (userUpdateError) {
            console.warn(
              "Error updating user table for patient:",
              userUpdateError
            );

            // Don't fail patient update if User Management update fails
          }
        } catch (userUpdateError) {
          console.warn("Error updating user table for patient:", userUpdateError);
          // Don't fail the whole operation if user update fails
        }
      }
    }
    
    return response.data;
  } catch (error) {
    console.error("Error updating patient:", error);
    throw error;
  }
};

export const searchPatientsOnName = async (name) => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    const response = await apiClient.get(`${API_URL}/search`, { params: { name } });
    return response.data;
  } catch (error) {
    console.error("Error searching patients:", error);
    throw error;
  }
};
 
export const deletePatient = async (id) => {
  try {
    // apiClient will automatically add organizationId as query param via interceptor
    const response = await apiClient.delete(`${API_URL}/${id}`);
    return response.data;
  } catch (error) {
    console.error("Error Deleting patient:", error);
    throw error;
  }
};

export const getPatientById = async (patientId, options = {}) => {
  // apiClient will automatically add organizationId as query param via interceptor
  const organizationId = resolveRequestOrganizationId(options.organizationId);
  const response = await apiClient.get(
    `${API_URL}/${patientId}`,
    organizationId ? { params: { organizationId } } : undefined
  );
  const data = response.data;
  if (!data || typeof data !== "object") return data;
  // Some backends wrap the patient: { data: {...} } or { patient: {...} }
  if (data.id != null || data.patientId != null || data.name != null || data.mobile != null) {
    return data;
  }
  if (data.data && typeof data.data === "object") return data.data;
  if (data.patient && typeof data.patient === "object") return data.patient;
  return data;
};

const unwrapPatientLookup = (data) => {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (Array.isArray(data.data)) return data.data;
  if (Array.isArray(data.patients)) return data.patients;
  if (data.data && typeof data.data === "object") return [data.data];
  if (data.patient && typeof data.patient === "object") return [data.patient];
  if (typeof data === "object") return [data];
  return [];
};

export const getPatientByUserId = async (userId, options = {}) => {
  try {
    const organizationId = resolveRequestOrganizationId(options.organizationId);
    const response = await apiClient.get(
      `${API_URL}/user/${userId}`,
      organizationId ? { params: { organizationId } } : undefined
    );
    const candidates = unwrapPatientLookup(response.data);
    const wantedMobile = String(options.mobile || "").replace(/\D/g, "").slice(-10);

    const matched = candidates.find((patient) => {
      if (!patient?.id) return false;
      const recordUserId = patient.userId ?? patient.user_id;
      if (recordUserId != null && String(recordUserId) !== String(userId)) {
        return false;
      }
      if (wantedMobile) {
        const recordMobile = String(patient.mobile || patient.contactNumber || "")
          .replace(/\D/g, "")
          .slice(-10);
        if (recordMobile && recordMobile !== wantedMobile) {
          return false;
        }
        if (recordUserId == null) {
          return recordMobile === wantedMobile;
        }
      } else if (recordUserId == null) {
        return false;
      }
      return true;
    });

    return matched || null;
  } catch (error) {
    const status = error?.response?.status;
    const message = String(error?.response?.data?.message || error?.message || "");
    const isPortalOrgLookup = resolveRequestOrganizationId(options.organizationId) != null;
    // Portal: missing {org}_patient row can return 500 instead of 404. Staff lookups still throw.
    if (
      status === 404 ||
      (isPortalOrgLookup &&
        status === 500 &&
        (message.includes("Failed to retrieve") ||
          message.includes("not found") ||
          message.includes("from table")))
    ) {
      return null;
    }
    throw error;
  }
};

/**
 * Sync patient type after workflow events (appointment → OUT, assignment/admission → IN).
 * Appointment syncs always use { force: true } so type becomes OUT.
 * Pass { force: true } also for Basic/Intermediate packages where patient type must always be OUT.
 */
export const syncPatientTypeForWorkflow = async (patientId, targetType, options = {}) => {
  if (!patientId || !targetType) return null;
  if (targetType !== "IN" && targetType !== "OUT") return null;

  try {
    const current = await getPatientById(patientId);
    if (!current) return null;

    const currentType = current.patientType ?? current.patient_type ?? null;
    if (targetType === "OUT" && currentType === "IN" && !options.force) {
      return current;
    }
    if (currentType === targetType) {
      return current;
    }

    return await updatePatient(patientId, {
      name: current.name,
      mobile: current.mobile ?? null,
      email: current.email ?? null,
      aadhar: current.aadhar ?? null,
      address: current.address,
      dob: current.dob,
      gender: current.gender,
      patientType: targetType,
    });
  } catch (error) {
    console.warn(
      `[syncPatientTypeForWorkflow] Failed to set ${targetType} for patient ${patientId}:`,
      error
    );
    return null;
  }
};